import { useMemo, useState } from "react";
import { Check, ChevronDown, ListFilter, X } from "lucide-react";
import { cn } from "@/lib/cn";

export interface FilterablePage {
  id: string;
  slug: string;
  title: string;
}

/**
 * The Signals global "filter by page" control. Picking a page scopes EVERY
 * panel to that page (the analytics hooks switch to the page-scoped endpoints).
 * Default is "All pages" (whole property). Best-practice segment bar: a single
 * filter that combines all the panels, not per-table search.
 */
export function PageFilter({
  pages,
  value,
  onChange,
}: {
  pages: FilterablePage[];
  value: string | null;
  onChange: (id: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");

  const selected = value ? pages.find((p) => p.id === value) ?? null : null;
  const shown = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return pages;
    return pages.filter((p) => p.slug.toLowerCase().includes(t) || p.title.toLowerCase().includes(t));
  }, [pages, q]);

  return (
    <div className="relative">
      <div
        className={cn(
          "inline-flex items-center rounded-full border bg-paper text-[13px] shadow-sm transition-colors dark:bg-white/5 dark:shadow-none",
          selected ? "border-accent/40" : "border-hairline",
        )}
      >
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="inline-flex items-center gap-1.5 py-1.5 pl-3 pr-2"
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <ListFilter className={cn("h-3.5 w-3.5", selected ? "text-accent" : "text-subtle")} strokeWidth={1.75} />
          <span className={cn("max-w-[180px] truncate font-medium", selected ? "text-ink-strong" : "text-muted")}>
            {selected ? selected.slug : "All pages"}
          </span>
          <ChevronDown className="h-3.5 w-3.5 text-subtle" strokeWidth={2} />
        </button>
        {selected && (
          <button
            type="button"
            aria-label="Clear page filter"
            onClick={() => onChange(null)}
            className="mr-1 grid h-6 w-6 place-items-center rounded-full text-subtle transition-colors hover:bg-paper-2 hover:text-ink dark:hover:bg-white/10"
          >
            <X className="h-3.5 w-3.5" strokeWidth={2} />
          </button>
        )}
      </div>

      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} aria-hidden />
          <div className="absolute right-0 z-40 mt-2 w-72 overflow-hidden rounded-card border border-hairline bg-paper shadow-lg dark:bg-ink-2">
            <div className="border-b border-hairline p-2">
              <input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Find a page…"
                className="w-full rounded-btn border border-hairline bg-canvas px-2.5 py-1.5 text-[13px] text-ink outline-none focus:border-accent/50 dark:bg-white/5"
              />
            </div>
            <ul className="max-h-72 overflow-auto p-1" role="listbox">
              <Row
                label="All pages"
                sub="Whole property"
                active={!value}
                onClick={() => {
                  onChange(null);
                  setOpen(false);
                }}
              />
              {shown.map((p) => (
                <Row
                  key={p.id}
                  label={p.slug}
                  sub={p.title}
                  active={value === p.id}
                  onClick={() => {
                    onChange(p.id);
                    setOpen(false);
                  }}
                />
              ))}
              {shown.length === 0 && (
                <li className="px-2.5 py-3 text-center text-[12px] text-subtle">No pages match “{q}”.</li>
              )}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}

function Row({ label, sub, active, onClick }: { label: string; sub?: string; active: boolean; onClick: () => void }) {
  return (
    <li>
      <button
        type="button"
        role="option"
        aria-selected={active}
        onClick={onClick}
        className={cn(
          "flex w-full items-center gap-2 rounded-btn px-2.5 py-1.5 text-left transition-colors",
          active ? "bg-accent-soft" : "hover:bg-paper-2 dark:hover:bg-white/5",
        )}
      >
        <span className="min-w-0 flex-1">
          <span className={cn("block truncate font-mono text-[12px]", active ? "text-accent" : "text-ink")}>{label}</span>
          {sub && <span className="block truncate text-[11px] text-subtle">{sub}</span>}
        </span>
        {active && <Check className="h-3.5 w-3.5 shrink-0 text-accent" strokeWidth={2.25} />}
      </button>
    </li>
  );
}
